import { LOCALE_LABELS, LOCALES, Markdown, RADIUS } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { CoverField } from "~/components/covers/CoverField";
import type { CoverPublishing } from "~/components/covers/use-cover-publishing";
import { slugify } from "~/lib/slug";
import type { CoverContext } from "~/types/covers-admin";
import type {
  DashboardHighlightAdmin,
  DashboardHighlightAdminCreate,
} from "~/types/dashboard-highlights-admin";

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

interface DashboardHighlightFormProps {
  initial?: DashboardHighlightAdmin;
  busy: boolean;
  errorKey: string | null;
  submitLabel: string;
  onSubmit: (highlight: DashboardHighlightAdminCreate) => void;
  actions?: ReactNode;
  /** Uploads and generates covers from the form's unsaved fields. */
  covers?: CoverPublishing;
}

/** Article-like Dashboard Highlight editor with an exact Markdown preview. */
export function DashboardHighlightForm({
  initial,
  busy,
  errorKey,
  submitLabel,
  onSubmit,
  actions,
  covers,
}: DashboardHighlightFormProps) {
  const { t } = useTranslation();
  const [locale, setLocale] = useState(initial?.locale ?? "cs");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(initial !== undefined);
  const [summary, setSummary] = useState(initial?.summary ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [coverImageUrl, setCoverImageUrl] = useState(
    initial?.cover_image_url ?? "",
  );
  const [isVisible, setIsVisible] = useState(initial?.is_visible ?? false);

  const slugInvalid = slug.length > 0 && !SLUG_PATTERN.test(slug);
  const changeTitle = (next: string) => {
    setTitle(next);
    if (!slugTouched) setSlug(slugify(next));
  };

  const coverContext: CoverContext = {
    slug,
    title,
    excerpt: summary,
    topics: [],
    keywords: [],
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (slugInvalid || slug.length === 0) return;
    onSubmit({
      slug,
      locale,
      title: title.trim(),
      summary: summary.trim(),
      body,
      cover_image_url: coverImageUrl.trim() || null,
      is_visible: isVisible,
    });
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 5fr)" },
        gap: { xs: 4, md: 6 },
        alignItems: "start",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          select
          label={t("highlightEditor.fields.locale")}
          value={locale}
          onChange={(event) => setLocale(event.target.value)}
          slotProps={{ htmlInput: { "data-testid": "highlight.locale" } }}
          sx={{ maxWidth: 240 }}
        >
          {LOCALES.map((option) => (
            <MenuItem key={option} value={option}>
              {LOCALE_LABELS[option]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={t("highlightEditor.fields.title")}
          value={title}
          onChange={(event) => changeTitle(event.target.value)}
          required
          fullWidth
          slotProps={{
            htmlInput: { "data-testid": "highlight.title", maxLength: 255 },
          }}
        />

        <TextField
          label={t("highlightEditor.fields.slug")}
          value={slug}
          onChange={(event) => {
            setSlugTouched(true);
            setSlug(event.target.value);
          }}
          required
          fullWidth
          error={slugInvalid}
          helperText={t("highlightEditor.fields.slugHelper")}
          slotProps={{
            htmlInput: { "data-testid": "highlight.slug", maxLength: 160 },
          }}
        />

        <TextField
          label={t("highlightEditor.fields.summary")}
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          required
          multiline
          minRows={3}
          fullWidth
          helperText={t("highlightEditor.fields.summaryHelper")}
          slotProps={{
            htmlInput: { "data-testid": "highlight.summary", maxLength: 500 },
          }}
        />

        <TextField
          label={t("highlightEditor.fields.body")}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          required
          multiline
          minRows={12}
          fullWidth
          helperText={t("highlightEditor.fields.bodyHelper")}
          slotProps={{ htmlInput: { "data-testid": "highlight.body" } }}
        />

        <CoverField
          value={coverImageUrl}
          onChange={setCoverImageUrl}
          label={t("highlightEditor.fields.coverImageUrl")}
          helperText={t("highlightEditor.fields.coverImageUrlHelper")}
          inputTestId="highlight.cover"
          testIdPrefix="highlight"
          disabled={busy}
          busy={covers?.busy}
          onUpload={
            covers &&
            ((file, options) => covers.upload(file, options, coverContext))
          }
          onGenerate={
            covers && ((subject) => covers.generate(subject, coverContext))
          }
        />

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={isVisible}
                onChange={(event) => setIsVisible(event.target.checked)}
                data-testid="highlight.is-visible"
              />
            }
            label={t("highlightEditor.fields.isVisible")}
          />
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {t("highlightEditor.fields.isVisibleHelper")}
          </Typography>
        </Box>

        {isVisible ? (
          <Alert severity="info" data-testid="highlight.visibility-note">
            {t("highlightEditor.visibilityNote", {
              locale: LOCALE_LABELS[locale as keyof typeof LOCALE_LABELS],
            })}
          </Alert>
        ) : null}

        {errorKey ? (
          <Alert severity="error" data-testid="highlight.error">
            {t(errorKey)}
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy}
            data-testid="highlight.submit"
          >
            {submitLabel}
          </Button>
          {actions}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          position: { md: "sticky" },
          top: { md: 24 },
        }}
      >
        <Box>
          <Typography variant="OV1" sx={{ color: "text.muted" }}>
            {t("highlightEditor.preview")}
          </Typography>
          <Typography variant="T3" sx={{ color: "text.muted", marginTop: 0.5 }}>
            {t("highlightEditor.previewHint")}
          </Typography>
        </Box>
        <Box
          data-testid="highlight.preview"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: `${RADIUS.lg}px`,
            padding: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          {coverImageUrl.trim() ? (
            <Box
              component="img"
              src={coverImageUrl.trim()}
              alt=""
              sx={{
                width: "100%",
                aspectRatio: "16 / 9",
                objectFit: "cover",
                borderRadius: `${RADIUS.md}px`,
              }}
            />
          ) : null}
          <Typography variant="H2" sx={{ color: "text.primary" }}>
            {title}
          </Typography>
          {summary.trim() ? (
            <Typography variant="H3" sx={{ color: "text.secondary" }}>
              {summary}
            </Typography>
          ) : null}
          <Markdown>{body}</Markdown>
        </Box>
      </Box>
    </Box>
  );
}
